import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Alert, Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { getVideoCases } from '../api';
import { useEnv } from '../contexts/EnvContext';
import { useLiveActivity } from '../hooks/useLiveActivity';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import VideoLookup from './VideoLookup';

export const CASE_KINDS = {
  failure: 'Failed call',
  in_call_report: 'Report in the call',
  post_call_report: 'Report after the call',
};

const when = (d) => (d ? dayjs(d).format('MMM D, h:mm A') : '—');
export const age = (ms) => {
  const hours = Math.floor(ms / 3600000);
  if (hours >= 48) return `${Math.floor(hours / 24)} days`;
  if (hours >= 1) return `${hours} h`;
  return `${Math.max(1, Math.floor(ms / 60000))} min`;
};

/**
 * Every support case (admin panel plan RPT-14): open ones oldest first, as
 * Bidsy's route 42 answers them. There is no deadline: a case waits for a
 * person for as long as it takes.
 */
export default function VideoCases() {
  const { env } = useEnv();
  const { refresh: refreshLive } = useLiveActivity();
  const [state, setState] = useState('open');
  const [kind, setKind] = useState('');
  const [view, setView] = useState({ loading: true, data: null, error: null, notice: null });
  const latest = useRef(0);

  const load = useCallback(async () => {
    const asked = ++latest.current;
    // Rows of the other filter are not shown while this one loads.
    setView({ loading: true, data: null, error: null, notice: null });
    try {
      const answer = await getVideoCases({ state, ...(kind ? { kind } : {}) });
      if (asked !== latest.current) return;
      setView({ loading: false, data: answer.data, error: null, notice: null });
      refreshLive();
    } catch (e) {
      if (asked !== latest.current) return;
      const body = e?.response?.data;
      if (e?.response?.status === 404) setView({ loading: false, data: null, error: null, notice: body?.message || 'Not on this environment yet.' });
      else setView({ loading: false, data: null, error: body?.message || e?.message || 'Could not load the cases', notice: null });
    }
  }, [state, kind, refreshLive]);

  useEffect(() => { load(); }, [load, env]);

  const counts = view.data?.counts || null;
  const from = { label: 'Video cases', path: '/video-cases' };

  return (
    <>
      <PageHeader
        title="Video cases"
        subtitle={counts
          ? `${Object.values(counts).reduce((sum, n) => sum + Number(n || 0), 0)} open: ${Object.entries(CASE_KINDS).map(([key, label]) => `${counts[key] || 0} ${label.toLowerCase()}`).join(', ')}`
          : 'Support cases from failed calls and reports'}
      />
      <Paper sx={{ p: 2, mb: 2 }}>
        <VideoLookup from={from} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToggleButtonGroup size="small" exclusive value={state} onChange={(e, next) => next && setState(next)}>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="settled">Settled</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={kind} onChange={(e, next) => setKind(next ?? '')}>
            <ToggleButton value="">All</ToggleButton>
            {Object.entries(CASE_KINDS).map(([key, label]) => <ToggleButton key={key} value={key}>{label}</ToggleButton>)}
          </ToggleButtonGroup>
          <Button variant="outlined" onClick={load}>Refresh</Button>
        </Box>
      </Paper>

      {view.loading && !view.data ? <LoadingBlock height={240} /> : null}
      {view.error ? <ErrorBlock message={view.error} onRetry={load} /> : null}
      {view.notice ? <Alert severity="info">{view.notice}</Alert> : null}
      {view.data ? (
        <Paper sx={{ p: 2 }}>
          {view.data.cases.length ? (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="video cases">
                <TableHead>
                  <TableRow>
                    <TableCell>Case</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Cause</TableCell>
                    <TableCell>Reporter</TableCell>
                    <TableCell>Reason or category</TableCell>
                    <TableCell>{state === 'open' ? 'Waiting' : 'Settled'}</TableCell>
                    <TableCell>People</TableCell>
                    <TableCell>Call</TableCell>
                    <TableCell align="right">Holds</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {view.data.cases.map((kase) => (
                    <TableRow key={kase.id} hover>
                      <TableCell><Link to={`/video-cases/${kase.id}`} state={{ from }}>Case #{kase.id}</Link></TableCell>
                      <TableCell>{CASE_KINDS[kase.kind] || kase.kind}</TableCell>
                      <TableCell>{kase.cause}</TableCell>
                      <TableCell>{kase.reporter_side || '—'}</TableCell>
                      <TableCell>{kase.reason_label || '—'}</TableCell>
                      <TableCell>{state === 'open' ? age(kase.age_ms) : `${when(kase.settled_at)} · ${kase.outcome} by ${kase.settled_by}`}</TableCell>
                      <TableCell>
                        {kase.fan?.username} with {kase.creator?.username}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <Link to={`/video-bookings/${kase.booking_id}`} state={{ from }}>Video call #{kase.booking_id}</Link>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {when(kase.call?.starts_at)}
                        {kase.call?.call_length_minutes ? ` · ${kase.call.call_length_minutes} min` : ''}
                      </TableCell>
                      <TableCell align="right">{Number(kase.tokens_held || 0).toLocaleString()} tokens</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">{state === 'open' ? 'No open case.' : 'No settled case.'}</Typography>
          )}
        </Paper>
      ) : null}
    </>
  );
}
